import { useEffect, useState, useCallback } from "react";
import { Lock, KeyRound, Eye, EyeOff } from "lucide-react";
import { login, getToken, verifyToken, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AuthGate = ({ children }) => {
  // null = checking, true = authed, false = locked
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setStatus(false);
      return;
    }
    const ok = await verifyToken();
    setStatus(ok);
    if (!ok) clearToken();
  }, []);

  useEffect(() => {
    check();
    const onUnauth = () => setStatus(false);
    window.addEventListener("auth:unauthorized", onUnauth);
    return () => window.removeEventListener("auth:unauthorized", onUnauth);
  }, [check]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await login(password);
      setPassword("");
      setStatus(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Login failed. Try again.";
      setError(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="auth-gate-loading"
      >
        <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.2em]">
          Verifying…
        </div>
      </div>
    );
  }

  if (status === true) return children;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white px-6"
      data-testid="auth-gate"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#09090b] text-white flex items-center justify-center">
            <Lock size={18} />
          </div>
          <div>
            <div className="font-display text-xl font-black leading-none tracking-tighter">
              SCREEN/01
            </div>
            <div className="label-xs mt-1">Restricted Access</div>
          </div>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-black leading-[0.95] tracking-tighter mb-3">
          Enter password
        </h1>
        <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
          This bureau is private. Enter the owner passphrase to continue.
        </p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
          <div>
            <label className="label-xs block mb-2">Passphrase</label>
            <div className="relative">
              <KeyRound
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
              />
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                placeholder="••••••••"
                disabled={submitting}
                data-testid="auth-password-input"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="current-password"
                inputMode="text"
                className="pl-9 pr-10 h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-[#09090b] font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="auth-toggle-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-[#09090b] transition-colors"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {error ? (
            <div
              className="border border-red-300 bg-red-50 text-red-700 text-xs font-mono px-3 py-2"
              data-testid="auth-error"
            >
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={submitting || !password.trim()}
            data-testid="auth-submit"
            className="w-full h-11 rounded-none bg-[#09090b] hover:bg-zinc-800 text-white uppercase tracking-[0.18em] text-xs font-semibold"
          >
            {submitting ? "Unlocking…" : "Unlock"}
          </Button>
        </form>

        <div className="mt-12 pt-6 border-t border-zinc-200 font-mono text-[11px] text-zinc-400 leading-relaxed">
          Session persists 30 days on this device.
          <br />
          After 5 wrong attempts you’ll be locked out for 15 min.
        </div>
      </div>
    </div>
  );
};

export default AuthGate;
